import { onCleanup, Show, splitProps, type JSX, type ParentProps } from "solid-js"
import "./tokens.css"
import "./skeleton.css"

// Loading skeletons (DESIGN-LOG §63): what a page or a part of one shows until its first read is in. The real boxes,
// rules and headings, and pale bars where the text will land, so nothing moves when it does. Only for a first load:
// polls and refetches keep what is drawn.
//
// The bars fade in after WAIT ms, so a fast load never flashes grey. A skeleton that follows another one (the sign-in
// outline, then the page's code, then its first read) or shows beside one keeps that wait and that pulse: one loading
// reads as one skeleton, never a blink between them.
const WAIT = 180
const FADE = 160
let showing = 0
let ended = Number.NEGATIVE_INFINITY
let shownAt = 0

// The style a skeleton's root carries: when its bars fade in, and the pulse's place on the shared clock.
export function skeletonTiming(): JSX.CSSProperties {
  const now = performance.now()
  if (!showing && now - ended > 120) shownAt = now + WAIT
  showing++
  onCleanup(() => {
    showing--
    ended = performance.now()
  })
  return { "--dx-skel-in": `${Math.round(shownAt - now)}ms`, "--dx-skel-pulse": `${Math.round(shownAt + FADE - now)}ms` }
}

// A region still loading: busy for screen readers, which hear its one label (when it has its own); the shapes are
// theirs to skip. `class` gives it the layout of what it stands in for.
export function Skeleton(props: ParentProps<{ label?: string; class?: string; style?: JSX.CSSProperties } & Omit<JSX.HTMLAttributes<HTMLDivElement>, "style">>) {
  const [own, rest] = splitProps(props, ["label", "class", "style", "children"])
  const timing = skeletonTiming()
  return (
    <div {...rest} class={own.class ? `dx-skel ${own.class}` : "dx-skel"} aria-busy="true" style={{ ...timing, ...own.style }}>
      <Show when={own.label}><span class="sr-only" role="status">{own.label}</span></Show>
      {own.children}
    </div>
  )
}

// One pale bar. `w` its width; `line` (the default) takes the height of the line of text it stands in for; `round` an
// icon or a mark. Extra classes give it a control's shape (a button, a pill). `timed`: a bar on its own in a page that
// has loaded (not inside a Skeleton) keeps the skeletons' wait and pulse.
export function Bar(props: { w?: string | number; h?: number; line?: boolean; round?: boolean; timed?: boolean; class?: string } & Omit<JSX.HTMLAttributes<HTMLElement>, "style">) {
  const [own, rest] = splitProps(props, ["w", "h", "line", "round", "timed", "class"])
  const size = (value?: string | number) => (typeof value === "number" ? `${value}px` : value)
  const timing = own.timed ? skeletonTiming() : undefined
  return (
    <i {...rest} class={own.class ? `dx-skel-bar ${own.class}` : "dx-skel-bar"} aria-hidden="true"
      data-line={(own.line ?? (!own.round && own.h === undefined)) || undefined} data-round={own.round || undefined}
      style={{ ...timing, width: size(own.w), height: size(own.h) }} />
  )
}
